import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Lock, AlertTriangle, CalendarPlus, Eraser, History, GraduationCap } from "lucide-react";
import { toMin, fromMin } from "./PlannerBoardGrid";

// V1.84 · Planificador multi-semana de un alumno: 6 semanas desplegables con
// una fila por día para meter de golpe el pack de clases que compra el alumno.
// Mismo punto de encuentro habitual para todas las citas y Madrid de circuito.
const PACKS = [1, 2, 3, 5, 10, 15, 20, 25, 30];
const DAY_ES = {
  monday: "Lunes", tuesday: "Martes", wednesday: "Miércoles",
  thursday: "Jueves", friday: "Viernes", saturday: "Sábado", sunday: "Domingo",
};
const MONTH_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const dm = (iso) => {
  const [, m, d] = String(iso || "").split("-");
  return `${Number(d)} ${MONTH_ES[Number(m) - 1] || ""}`;
};
const dmy = (iso) => String(iso || "").split("-").reverse().join("-");

const covers = (ranges, s, e) => (ranges || []).some((r) => toMin(r.start) <= s && toMin(r.end) >= e);
const overlaps = (ranges, s, e) => (ranges || []).some((r) => s < toMin(r.end) && toMin(r.start) < e);

function DayRow({ day, slots, slotMinutes, picked, onPick, nowMin, todayISO }) {
  return (
    <tr className="border-t border-neutral-100" data-testid={`week-day-${day.date}`}>
      <td className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white z-10">
        <span className="text-[11px] font-semibold text-neutral-800">{DAY_ES[day.weekday]}</span>
        <span className="ml-1 text-[10px] font-mono text-neutral-500">{dm(day.date)}</span>
        {day.holiday && <span className="ml-1 text-[9px] font-bold text-red-600">festivo</span>}
        {day.incident && <span className="ml-1 text-[9px] font-bold text-red-700" title={day.incident}>● baja</span>}
      </td>
      {slots.map((s, idx) => {
        const st = toMin(s);
        const en = st + slotMinutes;
        const mine = overlaps(day.busy, st, en);
        const available = covers(day.availability, st, en);
        const clash = overlaps(day.teacher_busy, st, en);
        const isBlock = (day.teacher_busy || []).some(
          (r) => r.kind === "block" && st < toMin(r.end) && toMin(r.start) < en);
        const gone = day.date === todayISO && en <= nowMin; // hora ya pasada hoy
        const sel = picked.has(`${day.date}|${idx}`);
        let cls = "bg-rose-100 hover:bg-rose-200 text-rose-400";
        if (available) cls = "bg-emerald-200 hover:bg-emerald-300 text-emerald-800";
        if (clash) cls = "bg-amber-400 hover:bg-amber-500 text-amber-950"; // solape dorado
        if (isBlock) cls = "bg-amber-600 text-white";
        if (mine) cls = "bg-neutral-400 text-neutral-700 cursor-not-allowed";
        if (gone) cls = "bg-neutral-100 text-neutral-300 cursor-not-allowed";
        if (sel) cls = "bg-neutral-900 text-white ring-2 ring-orange-500";
        return (
          <td key={s} className="p-0">
            <button
              type="button"
              data-testid={`week-cell-${day.date}-${idx}`}
              disabled={mine || gone}
              onClick={() => onPick(day.date, idx)}
              title={
                mine ? "El alumno ya tiene clase a esta hora"
                  : gone ? "Hora ya pasada"
                  : isBlock ? "Bloqueo de indisponibilidad del profesor"
                  : clash ? "Solapa con otra clase del profesor"
                  : available ? "Disponible" : "Fuera de su disponibilidad declarada"
              }
              className={"w-full h-7 text-[9px] font-mono font-semibold border-r border-white/70 transition-colors " + cls}
            >
              {mine ? <Lock className="h-2.5 w-2.5 mx-auto" />
                : sel ? "✓"
                : gone ? "·"
                : clash ? <AlertTriangle className="h-2.5 w-2.5 mx-auto" />
                : toMin(s) % 60 === 0 ? String(Math.floor(toMin(s) / 60)) : "·"}
            </button>
          </td>
        );
      })}
    </tr>
  );
}

export default function StudentWeekPlanner({
  studentId, teacher, slotMinutes = 45, gridStart, gridEnd, onCreated,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(new Set());
  const [openWeeks, setOpenWeeks] = useState(new Set([0]));
  const [pack, setPack] = useState(10);
  const [allowOverlap, setAllowOverlap] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/planner-board/student-weeks", {
        params: { student_id: studentId, teacher, weeks: 6, slot_minutes: slotMinutes,
          grid_start: gridStart, grid_end: gridEnd },
      });
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [studentId, teacher, slotMinutes, gridStart, gridEnd]);

  useEffect(() => { load(); }, [load]);

  const slots = data?.slots || [];

  const pick = (date, idx) => {
    setPicked((prev) => {
      const next = new Set(prev);
      const key = `${date}|${idx}`;
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Casillas contiguas del mismo día → una sola cita de N clases.
  const sessions = useMemo(() => {
    const byDate = new Map();
    for (const key of picked) {
      const [date, idx] = key.split("|");
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date).push(Number(idx));
    }
    const out = [];
    for (const [date, idxs] of [...byDate.entries()].sort()) {
      idxs.sort((a, b) => a - b);
      let i = 0;
      while (i < idxs.length) {
        let j = i;
        while (j + 1 < idxs.length && idxs[j + 1] === idxs[j] + 1) j += 1;
        out.push({
          date,
          time: slots[idxs[i]],
          end_time: fromMin(toMin(slots[idxs[j]]) + slotMinutes),
          classes: j - i + 1,
        });
        i = j + 1;
      }
    }
    return out;
  }, [picked, slots, slotMinutes]);

  const createAll = async () => {
    if (sessions.length === 0) return;
    if (afterExam.length > 0
      && !window.confirm(
        `Hay ${afterExam.length} clase(s) después del examen práctico del ${dmy(practicalExam.date)}.\n\n¿Las creo igualmente?`)) {
      return;
    }
    const pickup = data?.student?.pickup_name || data?.student?.neighborhood || "";
    if (!pickup) {
      toast.error("El alumno no tiene punto habitual de recogida: configúralo en Horarios");
      return;
    }
    setCreating(true);
    let ok = 0;
    const errors = [];
    for (const s of sessions) {
      try {
        await api.post("/appointments", {
          student_id: studentId,
          date: s.date,
          time: s.time,
          end_time: s.end_time,
          pickup_location: pickup,
          pickup_location_end: pickup,
          circuit: "Madrid",
          appointment_type: "clase_practica",
          teacher,
          override_unavailability: allowOverlap,
        });
        ok += 1;
      } catch (err) {
        errors.push(`${s.date} ${s.time}: ${formatApiError(err?.response?.data?.detail || err)}`);
      }
    }
    setCreating(false);
    if (ok) toast.success(`${ok} cita(s) creada(s) · punto de recogida ${pickup} · circuito Madrid`, { duration: 5000 });
    if (errors.length) toast.error(`${errors.length} cita(s) sin crear:\n${errors.slice(0, 3).join("\n")}`, { duration: 10000 });
    setPicked(new Set());
    await load();
    onCreated?.();
  };

  const totalPicked = picked.size;
  const remaining = data?.student?.stats?.remaining ?? 0;
  const theoryExam = data?.next_exams?.teorico || null;
  const practicalExam = data?.next_exams?.practico || null;
  // Clases seleccionadas que caen después del examen práctico convocado.
  const afterExam = useMemo(() => {
    if (!practicalExam?.date) return [];
    return [...picked].filter((k) => k.split("|")[0] > practicalExam.date);
  }, [picked, practicalExam]);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  if (loading) {
    return <div className="px-4 py-6 text-sm text-neutral-500" data-testid="week-planner-loading">Cargando semanas…</div>;
  }
  if (!data) return null;

  return (
    <div className="bg-neutral-50/70 border-y border-neutral-200 px-3 py-3 space-y-3" data-testid={`week-planner-${studentId}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">Clases contratadas</span>
        <div className="inline-flex rounded-lg border border-neutral-300 overflow-hidden bg-white">
          {PACKS.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`week-pack-${p}`}
              onClick={() => setPack(p)}
              className={"h-7 px-2.5 text-[11px] font-bold " +
                (pack === p ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100")}
            >
              {p}
            </button>
          ))}
        </div>
        {/* Cualquier número: a veces contratan 1, 2, 3… sueltas. */}
        <label className="inline-flex items-center gap-1 text-[11px] text-neutral-600">
          o
          <input
            type="number" min="1" max="99" value={pack}
            onChange={(e) => setPack(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
            data-testid="week-pack-custom"
            title="Escribe cualquier número de clases"
            className="w-14 h-7 rounded-lg border border-neutral-300 bg-white px-1.5 text-center text-[11px] font-bold font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          clases
        </label>
        <span
          data-testid="week-counter"
          className={"text-[11px] font-mono font-bold " +
            (totalPicked > pack ? "text-red-700" : totalPicked === pack ? "text-emerald-700" : "text-neutral-700")}
        >
          {totalPicked} / {pack} clases seleccionadas
        </span>
        <span className="text-[11px] text-neutral-500">
          Bono restante: <b className={remaining < 0 ? "text-red-700" : "text-neutral-800"}>{remaining}</b>
        </span>
        {totalPicked > remaining && (
          <span className="text-[11px] font-bold text-amber-700" data-testid="week-over-bono">
            supera el bono ({totalPicked - remaining} de más)
          </span>
        )}
        <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-600">
          <input
            type="checkbox" checked={allowOverlap} onChange={(e) => setAllowOverlap(e.target.checked)}
            data-testid="week-allow-overlap" className="h-3.5 w-3.5 accent-amber-600"
          />
          Permitir solapar bloqueos
        </label>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button" data-testid="week-clear" onClick={() => setPicked(new Set())}
            className="btn-ghost border border-neutral-200 !h-8 !text-[11px]"
          >
            <Eraser className="h-3.5 w-3.5" /> Limpiar
          </button>
          <button
            type="button" data-testid="week-create"
            disabled={sessions.length === 0 || creating}
            onClick={createAll}
            className="btn-primary !h-8 !text-[11px] disabled:opacity-40"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            {creating ? "Creando…" : `Crear ${sessions.length} cita(s) · ${totalPicked} clases`}
          </button>
        </div>
      </div>

      {/* V1.84b · Próximos exámenes y aviso si el planning se pasa del práctico. */}
      <div className="flex flex-wrap items-center gap-3 text-[11px]" data-testid="week-exams-bar">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 h-7"
          data-testid="week-next-theory">
          <GraduationCap className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-neutral-500">Próx. teórico:</span>
          <b className={theoryExam ? "text-neutral-900" : "text-neutral-400"}>
            {theoryExam ? `${dmy(theoryExam.date)} ${theoryExam.time || ""}` : "sin convocar"}
          </b>
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 h-7"
          data-testid="week-next-practical">
          <GraduationCap className="h-3.5 w-3.5 text-orange-600" />
          <span className="text-neutral-500">Próx. práctico:</span>
          <b className={practicalExam ? "text-neutral-900" : "text-neutral-400"}>
            {practicalExam ? `${dmy(practicalExam.date)} ${practicalExam.time || ""}` : "sin convocar"}
          </b>
        </span>
        {afterExam.length > 0 && (
          <span
            data-testid="week-after-exam-alert"
            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-red-300 bg-red-50 px-2 h-7 font-bold text-red-800"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {afterExam.length} clase(s) después del examen práctico del {dmy(practicalExam.date)}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[10px] text-neutral-600">
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200" /> Disponible</span>        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-100" /> No disponible</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-400" /> Solapa con el profesor</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-600" /> Bloqueo</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-neutral-400" /> Ya tiene clase</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-neutral-900" /> Seleccionado</span>
        <span className="ml-auto text-neutral-500">
          Punto de recogida: <b>{data.student.pickup_name || data.student.neighborhood || "sin definir"}</b> · Circuito: <b>Madrid</b>
        </span>
      </div>

      {data.weeks.map((w, wi) => {
        const open = openWeeks.has(wi);
        const weekPicked = [...picked].filter((k) => w.days.some((d) => k.startsWith(`${d.date}|`))).length;
        return (
          <div key={w.start} className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
            <button
              type="button"
              data-testid={`week-toggle-${wi}`}
              onClick={() => setOpenWeeks((prev) => {
                const next = new Set(prev);
                if (next.has(wi)) next.delete(wi); else next.add(wi);
                return next;
              })}
              className="w-full flex items-center gap-2 px-3 h-9 text-left hover:bg-neutral-50"
            >
              {open ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-500" />}
              <span className="text-[11px] font-bold text-neutral-800">
                Semana del {dm(w.start)} al {dm(w.end)}
              </span>
              {weekPicked > 0 && (
                <span className="text-[10px] font-mono font-bold text-orange-700">{weekPicked} clases</span>
              )}
            </button>
            {open && (
              <div className="overflow-x-auto border-t border-neutral-100">
                <table className="min-w-max text-xs border-collapse">
                  <thead className="bg-neutral-50 text-neutral-500">
                    <tr>
                      <th className="px-2 py-1 text-left font-semibold sticky left-0 bg-neutral-50 z-10 min-w-[120px]">Día</th>
                      {slots.map((s) => (
                        <th key={s} className="px-0 py-1 font-mono text-[9px] text-center w-8">{s}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {w.days.map((d) => (
                      <DayRow
                        key={d.date} day={d} slots={slots} slotMinutes={slotMinutes}
                        picked={picked} onPick={pick} nowMin={nowMin} todayISO={today}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <button
          type="button"
          data-testid="week-past-toggle"
          onClick={() => setShowPast((v) => !v)}
          className="w-full flex items-center gap-2 px-3 h-9 text-left hover:bg-neutral-50"
        >
          {showPast ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-500" />}
          <History className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-[11px] font-bold text-neutral-800">Clases anteriores ({data.past.length})</span>
          <span className="text-[10px] text-neutral-500">· mismo listado que la pestaña Horarios del alumno</span>
        </button>
        {showPast && (
          <div className="max-h-56 overflow-y-auto divide-y divide-neutral-100" data-testid="week-past-list">
            {data.past.length === 0 && <div className="px-3 py-3 text-xs text-neutral-500">Sin clases anteriores.</div>}
            {data.past.map((a) => (
              <div key={a.id} className="px-3 py-1.5 flex items-center gap-3 text-[11px]">
                <span className="font-mono tabular-nums">{a.date.split("-").reverse().join("-")}</span>
                <span className="font-mono tabular-nums text-neutral-600">{a.time}–{a.end_time}</span>
                <span className={
                  "font-semibold " +
                  (a.status === "cancelled"
                    ? (a.cancel_consumes ? "text-red-700" : "text-emerald-700")
                    : a.attended === true ? "text-blue-700"
                    : a.attended === false ? "text-amber-700" : "text-neutral-500")
                }>
                  {a.status === "cancelled"
                    ? (a.cancel_consumes ? "Cancelada Resta" : "Cancelada No Resta")
                    : a.attended === true ? "Realizada"
                    : a.attended === false
                      ? (a.missed_exempt ? "No asistió · no resta" : "No asistió")
                      : "Pendiente de marcar"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
